import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Header } from "../components/Header";
import { getHistory, deleteHistoryEvent } from "../lib/api";
import { fmtDate } from "../lib/format";
import { ArrowLeft, PlusCircle, Repeat, XCircle, Trash2 } from "lucide-react";
import { STRATEGIES, STRATEGY_BY_KEY } from "../lib/strategies";

const EVENT_META = {
  create: { label: "OPEN",  color: "#00E676", icon: PlusCircle },
  roll:   { label: "ROLL",  color: "#00B8D4", icon: Repeat     },
  close:  { label: "CLOSE", color: "#71717A", icon: XCircle    },
};

const legFor = (h, side) => {
  const strike = h[`${side}_strike`];
  const exp = h[`${side}_expiration`];
  if (strike == null || !exp) return null;
  return `$${Number(strike).toFixed(2)}C · ${fmtDate(exp)}`;
};

export default function HistoryPage() {
  const [items, setItems] = useState([]);
  const [err, setErr] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null); // event object awaiting confirmation
  const [busy, setBusy] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const strategyKey = searchParams.get("strategy") || "all";
  const activeStrategy = strategyKey === "all" ? null : STRATEGY_BY_KEY[strategyKey];

  const refresh = () =>
    getHistory(activeStrategy?.key).then(setItems).catch((e) => setErr(e?.response?.data?.detail || "Failed"));

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [strategyKey]);

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    setBusy(true);
    try {
      await deleteHistoryEvent(pendingDelete.event_id);
      setItems((prev) => prev.filter((h) => h.event_id !== pendingDelete.event_id));
      toast.success("Event removed from timeline");
      setPendingDelete(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="App flex flex-col h-screen">
      <Header />
      <main className="flex-1 overflow-y-auto max-w-6xl mx-auto w-full px-6 py-6 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="label-cap">History</div>
            <h1 className="text-2xl font-bold tracking-tight mt-1">Position Event Log</h1>
            <p className="text-xs text-[#A1A1AA] mt-1 leading-relaxed max-w-lg">
              Every open, roll, and close event for your covered call positions, with the
              underlying price captured at each event.
            </p>
          </div>
          <Link to="/" className="btn-outline flex items-center gap-1.5" data-testid="history-back-btn">
            <ArrowLeft size={14} /> Dashboard
          </Link>
        </div>

        {err && (
          <div className="border border-[#FF3B30] bg-[rgba(255,59,48,0.06)] p-3 text-sm text-[#FFD9D6]">{err}</div>
        )}

        {/* Strategy filter */}
        <div className="flex items-center gap-1 flex-wrap" data-testid="history-strategy-filter">
          <button
            onClick={() => setSearchParams({})}
            className={`px-3 py-1 border text-[10px] font-bold uppercase tracking-widest transition-colors ${
              strategyKey === "all"
                ? "border-white bg-white text-black"
                : "border-[#27272A] text-[#A1A1AA] hover:border-white"
            }`}
          >
            All
          </button>
          {STRATEGIES.map((s) => (
            <button
              key={s.key}
              onClick={() => setSearchParams({ strategy: s.key })}
              data-testid={`history-strategy-${s.key}`}
              className={`px-3 py-1 border text-[10px] font-bold uppercase tracking-widest transition-colors ${
                strategyKey === s.key
                  ? "text-black"
                  : "border-[#27272A] text-[#A1A1AA] hover:border-current"
              }`}
              style={
                strategyKey === s.key
                  ? { background: s.accent, borderColor: s.accent }
                  : { color: s.accent, borderColor: "#27272A" }
              }
            >
              {s.short} · {s.label}
            </button>
          ))}
        </div>

        <div className="panel">
          <div className="overflow-x-auto">
            <table className="w-full text-xs grid-lines" data-testid="history-table">
              <thead>
                <tr className="text-left border-b border-[#3F3F46] label-cap">
                  <th className="px-3 py-2.5">When</th>
                  <th className="px-3 py-2.5">Event</th>
                  <th className="px-3 py-2.5">Symbol</th>
                  <th className="px-3 py-2.5 text-right">Underlying</th>
                  <th className="px-3 py-2.5">From</th>
                  <th className="px-3 py-2.5">To</th>
                  <th className="px-3 py-2.5 text-right">Net Credit</th>
                  <th className="px-3 py-2.5 text-right">Credit %</th>
                  <th className="px-3 py-2.5">Verdict</th>
                  <th className="px-3 py-2.5">Mode</th>
                  <th className="px-3 py-2.5 text-right w-10"></th>
                </tr>
              </thead>
              <tbody className="font-mono">
                {items.map((h) => {
                  // Legacy rows have no event_type — treat as roll
                  const eventType = h.event_type || "roll";
                  const meta = EVENT_META[eventType] || EVENT_META.roll;
                  const Icon = meta.icon;
                  const underlying = h.underlying_at_event ?? h.underlying_at_roll;
                  const fromLeg = legFor(h, "from");
                  const toLeg = legFor(h, "to");
                  const isRoll = eventType === "roll";
                  const hasCredit = (isRoll || eventType === "create") && h.net_credit != null;
                  return (
                    <tr
                      key={h.event_id}
                      className="border-b border-[#27272A]"
                      data-testid={`history-row-${eventType}`}
                    >
                      <td className="px-3 py-2 text-[#A1A1AA] whitespace-nowrap">
                        {new Date(h.ts).toLocaleString()}
                      </td>
                      <td className="px-3 py-2">
                        <span
                          className="inline-flex items-center gap-1 text-[10px] font-bold tracking-widest uppercase px-1.5 py-0.5 border"
                          style={{ color: meta.color, borderColor: meta.color }}
                        >
                          <Icon size={9} /> {meta.label}
                        </span>
                      </td>
                      <td className="px-3 py-2 font-bold">{h.symbol}</td>
                      <td className="px-3 py-2 text-right">
                        {underlying != null ? `$${Number(underlying).toFixed(2)}` : "—"}
                      </td>
                      <td className="px-3 py-2 text-[#A1A1AA]">{fromLeg || "—"}</td>
                      <td className="px-3 py-2">{toLeg || "—"}</td>
                      <td
                        className="px-3 py-2 text-right"
                        style={{
                          color: !hasCredit
                            ? "#71717A"
                            : h.net_credit >= 0
                            ? "#00E676"
                            : "#FF3B30",
                        }}
                      >
                        {hasCredit
                          ? `${h.net_credit >= 0 ? "+" : "-"}$${Math.abs(h.net_credit).toFixed(2)}`
                          : "—"}
                      </td>
                      <td className="px-3 py-2 text-right text-[#A1A1AA]">
                        {(isRoll || eventType === "create") && h.credit_pct != null
                          ? `${h.credit_pct.toFixed(2)}%`
                          : "—"}
                      </td>
                      <td className="px-3 py-2 text-[10px] uppercase tracking-wider text-[#A1A1AA]">
                        {isRoll && h.category ? h.category.replace("_", " ") : "—"}
                      </td>
                      <td className="px-3 py-2 text-[10px] uppercase tracking-wider text-[#A1A1AA]">
                        {isRoll
                          ? h.kept_old
                            ? "Kept old"
                            : "Replaced"
                          : h.contracts
                          ? `${h.contracts}× contracts`
                          : "—"}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <button
                          onClick={() => setPendingDelete(h)}
                          data-testid={`history-delete-${h.event_id}`}
                          title="Remove this event from the timeline"
                          className="text-[#71717A] hover:text-[#FF3B30] transition-colors p-1"
                        >
                          <Trash2 size={13} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {items.length === 0 && (
                  <tr>
                    <td colSpan={11} className="text-center text-[#71717A] p-8">
                      No events yet. Add a position on the Dashboard to start tracking.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {pendingDelete && (
          <div
            className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
            data-testid="history-delete-modal"
            onClick={() => !busy && setPendingDelete(null)}
          >
            <div
              className="panel max-w-md w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="panel-h">
                <div className="label-cap text-[#FF3B30]">Remove Event</div>
              </div>
              <div className="p-4 space-y-3 text-xs">
                <p className="text-[#A1A1AA] leading-relaxed">
                  This deletes the <span className="font-bold text-white">
                    {(pendingDelete.event_type || "roll").toUpperCase()}
                  </span> event for <span className="font-bold text-white">{pendingDelete.symbol}</span> on{" "}
                  <span className="font-mono">{new Date(pendingDelete.ts).toLocaleString()}</span> from your timeline.
                </p>
                <p className="text-[#71717A] leading-relaxed">
                  Your active positions on the Dashboard are <span className="font-bold">not</span> affected. Use this to clean up mistaken or orphaned rolls.
                </p>
                <div className="flex gap-2 pt-2">
                  <button
                    onClick={() => setPendingDelete(null)}
                    disabled={busy}
                    data-testid="history-delete-cancel"
                    className="btn-outline flex-1 py-2 disabled:opacity-40"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmDelete}
                    disabled={busy}
                    data-testid="history-delete-confirm"
                    className="flex-1 py-2 bg-[#FF3B30] text-white text-xs font-semibold uppercase tracking-widest hover:bg-[#E0342A] disabled:opacity-40"
                  >
                    {busy ? "Removing…" : "Remove Event"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
